import React, { useEffect, useMemo, useState } from 'react'
import {
  CAlert,
  CButton,
  CCloseButton,
  CFormFeedback,
  CFormLabel,
  CLoadingButton,
  CMultiSelect,
  COffcanvas,
  COffcanvasBody,
  COffcanvasHeader,
  COffcanvasTitle,
  CSpinner,
} from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import { cilWarning } from '@coreui/icons'
import CommonService from 'src/service/CommonService'
import ToastComponent from 'src/components/common/TaostComponent'
import { isSetUp } from './totrUtils'

const sameIds = (a, b) =>
  a.length === b.length && [...a].sort().join(',') === [...b].sort().join(',')

// Pick or edit one round's Team of the Round. `round` is the row from the rounds table; the
// panel is open while it is set.
const TotrTeamForm = ({ round, onClose, onSaved }) => {
  const [loading, setLoading] = useState(false)
  const [loader, setLoader] = useState(false)
  const [options, setOptions] = useState([])
  const [selectedIds, setSelectedIds] = useState([])
  const [submitted, setSubmitted] = useState(false)

  const roundNumber = round?.round
  const editing = isSetUp(round)

  useEffect(() => {
    if (!roundNumber) return
    setLoading(true)
    setOptions([])
    setSelectedIds([])
    setSubmitted(false)
    CommonService.totwSelectedPlayer(roundNumber)
      .then((result) => {
        const list = result.data || []
        setOptions(list)
        setSelectedIds(list.filter((o) => o.selected).map((o) => o.value))
      })
      .catch(() => ToastComponent('Could not load the players', 'error'))
      .finally(() => setLoading(false))
  }, [roundNumber])

  const initialIds = useMemo(() => options.filter((o) => o.selected).map((o) => o.value), [options])
  const playersChanged = !sameIds(initialIds, selectedIds)
  const playersError = submitted && selectedIds.length === 0 ? 'Pick at least one player' : null

  const save = () => {
    setSubmitted(true)
    if (selectedIds.length === 0) return
    setLoader(true)
    // The API replaces the team's players whenever it is sent a list, and that wipes their
    // points - so only send one when the selection actually changed.
    CommonService.saveTotw({ player: playersChanged ? selectedIds : [], round: roundNumber })
      .then((res) => {
        if (res.status === 200) {
          // The API's own message for this call talks about bonus cards.
          ToastComponent(`Round ${roundNumber} Team of the Round saved`, 'success')
          onSaved()
          onClose()
        } else {
          ToastComponent(res.message, 'error')
        }
      })
      .catch((e) => ToastComponent(e.response?.data?.message || 'Could not save the team', 'error'))
      .finally(() => setLoader(false))
  }

  return (
    <COffcanvas placement="end" visible={!!round} onHide={onClose} backdrop style={{ width: 480 }}>
      <COffcanvasHeader>
        <div>
          <COffcanvasTitle>
            {editing ? 'Edit' : 'Pick'} Round {roundNumber} Team
          </COffcanvasTitle>
          <div className="text-medium-emphasis small">
            Choose the players who made this round&apos;s standout team.
          </div>
        </div>
        <CCloseButton className="text-reset" onClick={onClose} />
      </COffcanvasHeader>
      <COffcanvasBody className="d-flex flex-column">
        {loading ? (
          <div className="d-flex align-items-center gap-2 text-medium-emphasis py-4">
            <CSpinner size="sm" />
            <span>Loading players...</span>
          </div>
        ) : (
          <div>
            <CFormLabel className="d-flex justify-content-between">
              <span>Players *</span>
              <span className="text-medium-emphasis fw-normal">{selectedIds.length} selected</span>
            </CFormLabel>
            <CMultiSelect
              options={options}
              selectionType="tags"
              placeholder="Search players..."
              searchNoResultsLabel="No players found"
              onChange={(selected) =>
                setSelectedIds(Array.isArray(selected) ? selected.map((o) => o.value) : [])
              }
            />
            {playersError && (
              <CFormFeedback invalid className="d-block">
                {playersError}
              </CFormFeedback>
            )}
            {editing && playersChanged && selectedIds.length > 0 && (
              <CAlert color="warning" className="d-flex gap-2 border-0 small mt-2 mb-0">
                <CIcon icon={cilWarning} className="flex-shrink-0 mt-1" />
                <span>Changing the players resets the points already awarded to this team.</span>
              </CAlert>
            )}
          </div>
        )}
      </COffcanvasBody>
      <div className="d-flex justify-content-between gap-2 p-3 border-top">
        <CButton color="secondary" variant="outline" onClick={onClose}>
          Cancel
        </CButton>
        <CLoadingButton
          color="primary"
          loading={loader}
          disabled={loading || (editing && !playersChanged)}
          onClick={save}
        >
          {editing ? 'Save Changes' : 'Save Team'}
        </CLoadingButton>
      </div>
    </COffcanvas>
  )
}

export default TotrTeamForm
